'use client';

import * as React from 'react';
import { NumberField as NumberFieldPrimitive } from '@base-ui/react/number-field';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { cn } from '@/lib/utils';

const NumberField = NumberFieldPrimitive.Root;

const NumberFieldGroup = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof NumberFieldPrimitive.Group>
>(({ className, ...props }, ref) => (
  <NumberFieldPrimitive.Group
    ref={ref}
    data-slot="number-field-group"
    className={cn(
      'border-input focus-within:border-ring focus-within:ring-ring/50',
      'has-aria-invalid:ring-destructive/20 dark:has-aria-invalid:ring-destructive/40 has-aria-invalid:border-destructive',
      'flex h-8 w-full items-center rounded-sm border text-sm transition-colors',
      'focus-within:ring-[3px] has-aria-invalid:ring-[3px]',
      className
    )}
    {...props}
  />
));
NumberFieldGroup.displayName = 'NumberFieldGroup';

const NumberFieldInput = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<typeof NumberFieldPrimitive.Input>
>(({ className, ...props }, ref) => (
  <NumberFieldPrimitive.Input
    ref={ref}
    data-slot="number-field-input"
    className={cn(
      'dark:bg-input/30 bg-gray-100 placeholder:text-muted-foreground',
      'flex h-full w-full min-w-0 flex-1 px-3 py-1 text-center outline-none',
      'disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
      'focus:bg-primary-foreground',
      '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
      className
    )}
    {...props}
  />
));
NumberFieldInput.displayName = 'NumberFieldInput';

const NumberFieldIncrement = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof NumberFieldPrimitive.Increment>
>(({ className, children, ...props }, ref) => (
  <NumberFieldPrimitive.Increment
    ref={ref}
    data-slot="number-field-increment"
    className={cn(
      'text-muted-foreground hover:text-foreground hover:bg-accent',
      'flex h-full items-center justify-center px-2 transition-colors',
      'disabled:pointer-events-none disabled:opacity-50',
      'rounded-r-sm',
      className
    )}
    {...props}
  >
    {children ?? <ChevronUp className="size-4" />}
  </NumberFieldPrimitive.Increment>
));
NumberFieldIncrement.displayName = 'NumberFieldIncrement';

const NumberFieldDecrement = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof NumberFieldPrimitive.Decrement>
>(({ className, children, ...props }, ref) => (
  <NumberFieldPrimitive.Decrement
    ref={ref}
    data-slot="number-field-decrement"
    className={cn(
      'text-muted-foreground hover:text-foreground hover:bg-accent',
      'flex h-full items-center justify-center px-2 transition-colors',
      'disabled:pointer-events-none disabled:opacity-50',
      'rounded-l-sm',
      className
    )}
    {...props}
  >
    {children ?? <ChevronDown className="size-4" />}
  </NumberFieldPrimitive.Decrement>
));
NumberFieldDecrement.displayName = 'NumberFieldDecrement';

const NumberFieldScrubArea = NumberFieldPrimitive.ScrubArea;
const NumberFieldScrubAreaCursor = NumberFieldPrimitive.ScrubAreaCursor;

// Convenience component for common use case
interface NumberFieldSimpleProps
  extends Omit<
    React.ComponentProps<typeof NumberFieldPrimitive.Root>,
    'children'
  > {
  placeholder?: string;
  inputClassName?: string;
  showStepper?: boolean;
}

const NumberFieldSimple = React.forwardRef<
  HTMLDivElement,
  NumberFieldSimpleProps
>(
  (
    {
      className,
      placeholder,
      inputClassName,
      showStepper = true,
      disabled,
      ...props
    },
    ref
  ) => (
    <NumberField ref={ref} disabled={disabled} {...props}>
      <NumberFieldGroup className={className}>
        <NumberFieldInput
          placeholder={placeholder}
          className={cn('rounded-l-sm', inputClassName)}
        />
        {showStepper && (
          <div className="flex h-full flex-col border-l">
            <NumberFieldIncrement
              disabled={disabled}
              className="h-1/2 rounded-none rounded-tr-sm border-b px-1"
            >
              <ChevronUp className="size-3" />
            </NumberFieldIncrement>
            <NumberFieldDecrement
              disabled={disabled}
              className="h-1/2 rounded-none rounded-br-sm px-1"
            >
              <ChevronDown className="size-3" />
            </NumberFieldDecrement>
          </div>
        )}
      </NumberFieldGroup>
    </NumberField>
  )
);
NumberFieldSimple.displayName = 'NumberFieldSimple';

export {
  NumberField,
  NumberFieldGroup,
  NumberFieldInput,
  NumberFieldIncrement,
  NumberFieldDecrement,
  NumberFieldScrubArea,
  NumberFieldScrubAreaCursor,
  NumberFieldSimple,
};
